import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, PriceInput, Select, Textarea } from '@artosera/ui';
import type { AuteurEstimation } from '@artosera/shared';
import {
  messageErreurAssurance,
  useReevaluer,
  type OeuvreAssurancePresentee,
} from '../../api/assurance.js';
import { Field } from '../Field.js';
import { AuteurEstimationChamps } from './AuteurEstimationChamps.js';
import styles from './AssurancePage.module.css';

export function ReevaluationLotDialog({
  galerieSlug,
  compteId,
  oeuvres,
  onFermer,
}: {
  galerieSlug: string;
  compteId: string;
  oeuvres: OeuvreAssurancePresentee[];
  onFermer: () => void;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const geste = useReevaluer(galerieSlug);
  const [mode, setMode] = useState('pourcentage');
  const [pourcentage, setPourcentage] = useState('10');
  const [montants, setMontants] = useState<Record<string, number | null>>({});
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [auteur, setAuteur] = useState<AuteurEstimation>({ type: 'membre', compteId });
  const [justification, setJustification] = useState('');
  const [erreur, setErreur] = useState('');
  async function soumettre() {
    setErreur('');
    try {
      const commun = { dateEffet: new Date(date).toISOString(), auteur, justification };
      if (mode === 'pourcentage')
        await geste.mutateAsync({
          ...commun,
          mode,
          pourcentage: Number(pourcentage),
          oeuvreIds: oeuvres.map((o) => o._id),
        });
      else
        await geste.mutateAsync({
          ...commun,
          mode: 'tableau',
          lignes: oeuvres.map((o) => ({ oeuvreId: o._id, montantCentimes: montants[o._id]! })),
        });
      onFermer();
    } catch (e) {
      setErreur(messageErreurAssurance(e));
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onFermer();
      }}
      title={t('regie:assurance.reevaluer')}
      closeLabel={t('commun:actions.fermer')}
    >
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          void soumettre();
        }}
      >
        <Field htmlFor="assurance-mode" label={t('regie:assurance.mode')}>
          <Select
            id="assurance-mode"
            value={mode}
            onValueChange={setMode}
            options={['pourcentage', 'tableau'].map((value) => ({
              value,
              label: t(`regie:assurance.modes.${value}`),
            }))}
          />
        </Field>
        {mode === 'pourcentage' ? (
          <Field htmlFor="assurance-pourcentage" label={t('regie:assurance.pourcentage')}>
            <Input
              id="assurance-pourcentage"
              type="number"
              min={-100}
              step="any"
              required
              value={pourcentage}
              onChange={(e) => setPourcentage(e.target.value)}
            />
          </Field>
        ) : (
          oeuvres.map((o) => (
            <Field key={o._id} htmlFor={`montant-${o._id}`} label={o.titre}>
              <PriceInput
                optional
                id={`montant-${o._id}`}
                lang={i18n.language === 'en' ? 'en' : 'fr'}
                currency={o.devise}
                cents={montants[o._id] ?? null}
                onCentsChange={(v) => setMontants({ ...montants, [o._id]: v })}
              />
            </Field>
          ))
        )}
        <Field htmlFor="assurance-date" label={t('regie:assurance.dateEffet')}>
          <Input
            id="assurance-date"
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <AuteurEstimationChamps auteur={auteur} compteId={compteId} onChange={setAuteur} />
        <Field htmlFor="assurance-justification" label={t('regie:assurance.justification')}>
          <Textarea
            id="assurance-justification"
            maxLength={5000}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
          />
        </Field>
        {erreur && <p role="alert">{erreur}</p>}
        <Button
          type="submit"
          loading={geste.isPending}
          disabled={
            oeuvres.length === 0 ||
            oeuvres.length > 500 ||
            (mode === 'tableau' && oeuvres.some((o) => montants[o._id] == null))
          }
        >
          {t('commun:actions.enregistrer')}
        </Button>
      </form>
    </Dialog>
  );
}
